'use client';

import { forwardRef, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Box } from '@mui/material';

import { BottomFilterDrawer, ErrorState } from '@/components/common';
import { CryptoFinanceSidebarSkeleton } from '@/components/skeletons';
import { type CryptoFinanceFilter } from '@/services/api/cryptoFinance.api';
import { useCryptoFinanceSidebar } from '@/services/query/cryptoFinance.queries';
import { type CategoryType } from '@/types/category.types';
import { getIconFromServerString } from '@/utils/icon.utils';

import { CATEGORY_CONFIG } from '@/constants/categories.constants';

interface CryptoFinanceSidebarProps {
  category: CategoryType;
  activeFilter?: string;
  onFilterClick: (filter: CryptoFinanceFilter) => void;
  variant?: 'sidebar' | 'chips';
}

interface FilterItemProps {
  filter: CryptoFinanceFilter;
  isActive: boolean;
  onFilterClick: (filter: CryptoFinanceFilter) => void;
  showDivider: boolean;
  variant: 'sidebar' | 'chips';
}

const FilterItem = ({ filter, isActive, onFilterClick, showDivider, variant }: FilterItemProps) => {
  const handleClick = () => onFilterClick(filter);

  const icon = getIconFromServerString(filter.icon);

  const sidebarIconColor = isActive ? 'primary.main' : 'inherit';
  const sidebarFontWeight = isActive ? 700 : 600;
  const showCount = variant === 'sidebar' && filter.count > 0;

  if (variant === 'chips') {
    return (
      <ListItemButton
        onClick={handleClick}
        sx={{
          borderRadius: 1,
          p: 1,
          minWidth: 64,
          height: 64,
          transition: 'all 0.2s',
          bgcolor: isActive ? 'action.selected' : 'transparent',
          border: 'none',
          color: isActive ? 'text.primary' : 'text.secondary',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '&:hover': {
            bgcolor: isActive ? 'action.selected' : 'action.hover',
          },
        }}
      >
        <Box sx={{ display: 'flex', color: 'inherit' }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {filter.label}
        </Typography>
      </ListItemButton>
    );
  }

  return (
    <Box>
      <ListItemButton
        onClick={handleClick}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          py: 1.2,
          px: 2,
          transition: 'all 0.2s',
          bgcolor: isActive ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32, color: sidebarIconColor }}>{icon}</ListItemIcon>
        <ListItemText
          primary={filter.label}
          primaryTypographyProps={{
            fontSize: '1.1rem',
            fontWeight: sidebarFontWeight,
          }}
        />
        {showCount && (
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 1,
              fontSize: '1rem',
              fontWeight: 700,
            }}
          >
            {filter.count}
          </Typography>
        )}
      </ListItemButton>
      {showDivider && <Divider sx={{ my: 2, mx: 2 }} />}
    </Box>
  );
};

export const CryptoFinanceSidebar = forwardRef<HTMLDivElement, CryptoFinanceSidebarProps>(
  function CryptoFinanceSidebar(
    { category, activeFilter, onFilterClick, variant = 'sidebar' },
    ref
  ) {
    const { data: filters = [], isLoading, isError, refetch } = useCryptoFinanceSidebar(category);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const config = CATEGORY_CONFIG[category];

    const handleRefetch = () => refetch();
    const handleDrawerOpen = () => setIsDrawerOpen(true);
    const handleDrawerClose = () => setIsDrawerOpen(false);

    const isSidebarVariant = variant === 'sidebar';
    const isChipsVariant = variant === 'chips';

    const checkIsActive = (filterId: string) => {
      return activeFilter === filterId || (filterId === 'all' && !activeFilter);
    };

    const checkShowDivider = (filterId: string) => {
      return isSidebarVariant && config.dividerAfter === filterId;
    };

    const mapFilterToItem = (filter: CryptoFinanceFilter) => (
      <FilterItem
        key={filter.id}
        filter={filter}
        isActive={checkIsActive(filter.id)}
        onFilterClick={onFilterClick}
        showDivider={checkShowDivider(filter.id)}
        variant={variant}
      />
    );

    const mapDrawerFilterToItem = (filter: CryptoFinanceFilter) => {
      const handleDrawerFilterClick = (f: CryptoFinanceFilter) => {
        onFilterClick(f);
        handleDrawerClose();
      };
      const showDividerDrawer = config.dividerAfter === filter.id;

      return (
        <FilterItem
          key={filter.id}
          filter={filter}
          isActive={checkIsActive(filter.id)}
          onFilterClick={handleDrawerFilterClick}
          showDivider={showDividerDrawer}
          variant="sidebar"
        />
      );
    };

    const renderFilterItemList = filters.map(mapFilterToItem);
    const renderDrawerFilterItemList = filters.map(mapDrawerFilterToItem);

    const visibleFilters = filters.slice(0, 4);
    const hasMoreFilters = filters.length > 4;

    if (isLoading) {
      return <CryptoFinanceSidebarSkeleton variant={variant} />;
    }

    if (isError) {
      return (
        <Box
          sx={{
            width: isSidebarVariant ? 240 : '100%',
            flexShrink: 0,
            display: isSidebarVariant ? { xs: 'none', md: 'block' } : { xs: 'block', md: 'none' },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: isSidebarVariant ? '16px' : '12px',
            p: 2,
            bgcolor: 'background.paper',
          }}
        >
          <ErrorState title="Error" message="Failed to load filters" onRetry={handleRefetch} />
        </Box>
      );
    }

    if (isChipsVariant) {
      return (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              pb: 1,
              mx: -2,
              px: 2,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {visibleFilters.map(mapFilterToItem)}

            {hasMoreFilters && (
              <ListItemButton
                onClick={handleDrawerOpen}
                sx={{
                  borderRadius: 1,
                  p: 1,
                  minWidth: 64,
                  height: 64,
                  transition: 'all 0.2s',
                  bgcolor: 'transparent',
                  border: 'none',
                  color: 'text.secondary',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <MoreHorizIcon sx={{ fontSize: '1.25rem', color: 'inherit' }} />
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1 }}>
                  More
                </Typography>
              </ListItemButton>
            )}
          </Box>

          <BottomFilterDrawer open={isDrawerOpen} onClose={handleDrawerClose} title="All Filters">
            <Box>
              <List sx={{ p: 0 }}>{renderDrawerFilterItemList}</List>
            </Box>
          </BottomFilterDrawer>
        </Box>
      );
    }

    return (
      <Box
        ref={ref}
        sx={{
          width: 240,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          position: 'sticky',
          top: 0,
          height: '100%',
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '16px',
          p: 2,
          bgcolor: 'background.paper',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: '4px' },
        }}
      >
        <List sx={{ p: 0 }}>{renderFilterItemList}</List>
      </Box>
    );
  }
);
